import React, { useCallback, useEffect, useState } from "react";
import { FileText, Trash2, RefreshCw, Archive, ChevronDown } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { adminApi } from "../lib/api";
import StatusBadge from "../components/StatusBadge";

const TYPES = ["loi", "decret", "arrete", "circulaire", "ordonnance"];
const STATUSES = ["en_vigueur", "abroge", "modifie"];

export default function AdminDocuments() {
  const { t } = useI18n();
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (docType) params.document_type = docType;
      if (status) params.status = status;
      const { data } = await adminApi.listDocs(params);
      setDocs(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [docType, status]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const onChangeStatus = async (doc, newStatus) => {
    await adminApi.updateDoc(doc.id, { status: newStatus });
    fetchDocs();
  };

  const onDelete = async (doc) => {
    if (!window.confirm(t("documents.confirmDelete"))) return;
    await adminApi.deleteDoc(doc.id);
    fetchDocs();
  };

  return (
    <div data-testid="admin-documents-page">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">
          / corpus
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">
          {t("documents.title")}
        </h1>
        <p className="text-sm text-foreground/60 mt-2">{t("documents.subtitle")}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6" data-testid="docs-filters">
        <Select
          label={t("documents.filterType")}
          value={docType}
          onChange={setDocType}
          options={[
            { value: "", label: t("common.all") },
            ...TYPES.map((v) => ({ value: v, label: t(`types.${v}`) })),
          ]}
          testid="filter-type-select"
        />
        <Select
          label={t("documents.filterStatus")}
          value={status}
          onChange={setStatus}
          options={[
            { value: "", label: t("common.all") },
            ...STATUSES.map((v) => ({ value: v, label: t(`status.${v}`) })),
          ]}
          testid="filter-status-select"
        />
      </div>

      {loading && (
        <div className="text-sm text-foreground/50 font-mono mb-4" data-testid="docs-loading">
          {t("common.loading")}
        </div>
      )}

      {!loading && docs.length === 0 && (
        <div
          className="bg-white border border-primary/10 rounded-sm p-10 text-center text-foreground/50 text-sm"
          data-testid="docs-empty"
        >
          {t("documents.empty")}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4" data-testid="docs-list">
        {docs.map((d) => (
          <DocCard
            key={d.id}
            doc={d}
            onChangeStatus={onChangeStatus}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}

function DocCard({ doc, onChangeStatus, onDelete }) {
  const { t } = useI18n();
  return (
    <article
      className="group bg-white border border-primary/15 rounded-sm p-5 hover:-translate-y-0.5 hover:shadow-md transition-all relative"
      data-testid={`doc-card-${doc.id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 bg-primary/10 text-primary rounded-sm flex items-center justify-center shrink-0">
          <FileText size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/50">
              {t(`types.${doc.document_type}`, doc.document_type)}
            </span>
            <StatusBadge
              status={doc.status}
              label={t(`status.${doc.status}`, doc.status)}
            />
          </div>
          <h3 className="font-display text-base font-semibold text-foreground leading-tight">
            {doc.title}
          </h3>
          <div className="font-mono text-xs text-primary mt-1">{doc.reference}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center border-t border-primary/10 pt-3 mt-3">
        <MiniStat value={doc.chunks_count} label={t("documents.articles")} />
        <MiniStat value={doc.page_count} label={t("documents.pages")} />
        <MiniStat
          value={`${Math.round((doc.ocr_quality_score ?? 1) * 100)}%`}
          label={t("documents.quality")}
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {doc.status !== "abroge" && (
          <MiniBtn
            onClick={() => onChangeStatus(doc, "abroge")}
            icon={Archive}
            label={t("documents.markAbroge")}
            testid={`mark-abroge-${doc.id}`}
          />
        )}
        {doc.status !== "modifie" && (
          <MiniBtn
            onClick={() => onChangeStatus(doc, "modifie")}
            icon={RefreshCw}
            label={t("documents.markModifie")}
            testid={`mark-modifie-${doc.id}`}
          />
        )}
        {doc.status !== "en_vigueur" && (
          <MiniBtn
            onClick={() => onChangeStatus(doc, "en_vigueur")}
            icon={RefreshCw}
            label={t("documents.markEnVigueur")}
            testid={`mark-envigueur-${doc.id}`}
          />
        )}
        <MiniBtn
          danger
          onClick={() => onDelete(doc)}
          icon={Trash2}
          label={t("documents.delete")}
          testid={`delete-doc-${doc.id}`}
        />
      </div>
    </article>
  );
}

function MiniStat({ value, label }) {
  return (
    <div>
      <div className="font-display text-xl text-primary font-semibold">{value}</div>
      <div className="font-mono text-[9px] uppercase tracking-wider text-foreground/50">
        {label}
      </div>
    </div>
  );
}

function MiniBtn({ onClick, icon: Icon, label, danger, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-sm border transition-colors ${
        danger
          ? "border-[#FECACA] text-[#991B1B] hover:bg-[#FEE2E2]"
          : "border-primary/20 text-foreground/70 hover:border-primary hover:text-primary hover:bg-primary/5"
      }`}
    >
      <Icon size={12} />
      {label}
    </button>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="relative inline-flex flex-col">
      <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-foreground/50 mb-1">
        {label}
      </span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="appearance-none pr-8 pl-3 py-2 bg-white border border-primary/20 rounded-sm text-sm min-w-[160px] focus:border-primary outline-none cursor-pointer"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={14}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground/40 pointer-events-none"
        />
      </div>
    </label>
  );
}
